import { last, mapValues, omit } from "lodash";

import { type ExerciseAggregateStats } from "@fitness-app/data-models/entities/ExerciseStats";
import {
  PREVIOUS_SERIES_HINT,
  TrainingStatus,
  WeightHint,
  type ExerciseInWorkout,
  type ExerciseSet,
  type SetHintSource,
} from "@fitness-app/data-models/entities/TrainingProgram";

export const getPropertiesValue = (property: number | [number, number] | null | undefined, index?: number) => {
  if (typeof property === "undefined" || property === null) {
    return null;
  }
  if (Array.isArray(property)) {
    return property[typeof index === "number" ? index : 1];
  }
  return property;
};

interface HintValuesArgs {
  sets: Record<string, ExerciseSet>;
  hintType?: WeightHint;
  records?: ExerciseAggregateStats | null;
  exercise: ExerciseInWorkout;
  /**
   * When false (the default), the hint only prefills weight and duration and the trainer's planned
   * repeats are kept. Resolve it with `getWorkoutHintSettings` rather than passing a literal — an
   * omitted flag has to mean "leave the planned repeats alone", since that is what TRE-1824/1825
   * were about.
   */
  applyToRepeats?: boolean;
}

export interface EnhancedWorkoutSet extends ExerciseSet {
  withHint?: SetHintSource;
}

/**
 * The closest series before `key` that the client already marked as done in THIS workout.
 * Skipped series (REJECTED / PASSED) are stepped over, a series without a status ends the
 * search — nothing before it has been logged yet.
 */
const findFulfilledSeriesBefore = (sets: Record<string, ExerciseSet>, key: string): ExerciseSet | null => {
  const current = Number(key);
  const earlierKeys = Object.keys(sets)
    .map(Number)
    .filter((seriesNumber) => !Number.isNaN(seriesNumber) && seriesNumber < current)
    .sort((a, b) => b - a);

  for (const seriesNumber of earlierKeys) {
    const candidate = sets[String(seriesNumber)];
    if (candidate?.status === TrainingStatus.FULFILLED) {
      return candidate;
    }
  }
  return null;
};

const hasFulfilledSeries = (sets: Record<string, ExerciseSet>) =>
  Object.values(sets).some((set) => set?.status === TrainingStatus.FULFILLED);

const setHintValuesForSets = ({
  sets,
  hintType = WeightHint.DEFAULT,
  records = null,
  exercise,
  applyToRepeats = false,
}: HintValuesArgs): Record<string, EnhancedWorkoutSet | ExerciseSet> => {
  if (hintType === WeightHint.DEFAULT || (!records && !hasFulfilledSeries(sets))) {
    return sets;
  }
  return mapValues(sets, (set, key) => {
    const setStatusIsDefault = !set.status;
    let updatedSet: EnhancedWorkoutSet = {
      ...set,
    };
    if (setStatusIsDefault) {
      // TRE-2100: a series the client has just logged in this workout outranks any record. Before
      // this, series 2 and 3 kept showing the weight from the last time she did the exercise —
      // weeks or a whole stage earlier — after she had typed a different one into series 1, which
      // read as "random values" and "my weights don't save". Falls through to the history-based
      // hint only until the first series of the exercise is done.
      const previousSeries = findFulfilledSeriesBefore(sets, key);
      if (previousSeries) {
        return {
          ...updatedSet,
          weight: previousSeries.weight ?? updatedSet.weight,
          duration: updatedSet.duration && previousSeries.duration ? previousSeries.duration : updatedSet.duration,
          repeats: applyToRepeats ? (previousSeries.repeats ?? updatedSet.repeats) : updatedSet.repeats,
          withHint: PREVIOUS_SERIES_HINT,
        };
      }
      if (!records) {
        return updatedSet;
      }
      if (hintType === WeightHint.LATEST && records.latestWorkout.set) {
        const setKey = records.latestWorkout.set[key] ? key : last(Object.keys(records.latestWorkout.set));
        return {
          ...omit(records.latestWorkout.set[setKey ?? 1], ["status"]),
          duration:
            updatedSet.duration && !records.latestWorkout.set[setKey ?? 1]?.duration
              ? updatedSet.duration
              : records.latestWorkout.set[setKey ?? 1]?.duration,
          // LATEST carries over the last workout's repeats on top of the weight, but only when the
          // client opted into it — otherwise the planned repeats stay put so the workout list and
          // the exercise sheet agree. Falls back to the planned value when the last set has none.
          repeats: applyToRepeats
            ? (records.latestWorkout.set[setKey ?? 1]?.repeats ?? updatedSet.repeats)
            : updatedSet.repeats,
          withHint: WeightHint.LATEST,
        };
      }
      if (
        records.records.maxWeight &&
        (Array.isArray(updatedSet.weight) ||
          typeof updatedSet.weight === "number" ||
          typeof exercise.weight === "number")
      ) {
        const hintValue = hintType === WeightHint.MAX_VALUE ? records.records.maxWeight : records.latestWorkout.weight;

        const value = getPropertiesValue(hintValue);

        if (set.weight !== value && hintType !== WeightHint.LATEST) {
          updatedSet = {
            ...updatedSet,
            weight: value ?? 0,
            withHint: WeightHint.MAX_VALUE,
          };
        }
      }
      if (applyToRepeats && hintType === WeightHint.MAX_VALUE && records.records.maxRepeats) {
        // Indexing a [min, max] record can come back undefined, so normalise before comparing.
        const value = getPropertiesValue(records.records.maxRepeats) ?? null;

        if (value !== null && value !== set.repeats) {
          updatedSet = {
            ...updatedSet,
            repeats: value,
            withHint: WeightHint.MAX_VALUE,
          };
        }
      }
      if (records.records.maxDuration && updatedSet.duration) {
        const hintValue =
          hintType === WeightHint.MAX_VALUE
            ? records.records.maxDuration.value
            : (records.latestWorkout.duration?.value ?? 0);
        const value = getPropertiesValue(hintValue);
        if (typeof set.duration?.value === "number" && set.duration.value !== value) {
          updatedSet = {
            ...updatedSet,
            withHint: WeightHint.MAX_VALUE,
            duration: {
              value: value ?? 0,
              format: updatedSet.duration.format,
            },
          };
        }
      }
    }
    return updatedSet;
  });
};

export default setHintValuesForSets;
